import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";

// ════════════════════════════════════════════════════════════════════
//  SettingsDock — a floating, frosted category navigator.
//  Keeps the Dock feel (floating layer, depth/translucency, object-like
//  icon tiles, motion on hover) but stays readable: labels are ALWAYS
//  visible, and hover is a gentle lift — no janky cursor-fisheye.
//
//  Phones: the desktop dock is a sticky, viewport-tall side layer — on a
//  375px screen it pinned itself OVER the settings content and ate its
//  taps. On mobile it becomes a static, horizontally-scrolling chip strip
//  (icon + label) that scrolls the active chip into view.
// ════════════════════════════════════════════════════════════════════

export function SettingsDock({ items, active, onSelect }) {
  const isMobile = useIsMobile();
  const [hover, setHover] = useState(null);
  const stripRef = useRef(null);

  // Keep the selected chip visible in the strip (a deep-link can land on a
  // tab that sits off the right edge). scrollTo on the strip itself — never
  // scrollIntoView — so the page's vertical position is left alone.
  useEffect(() => {
    if (!isMobile) return;
    const strip = stripRef.current; if (!strip) return;
    const el = strip.querySelector(`[data-key="${active}"]`); if (!el) return;
    const left = el.offsetLeft - 12;
    const right = el.offsetLeft + el.offsetWidth + 12;
    if (left < strip.scrollLeft) strip.scrollTo({ left, behavior: "smooth" });
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollTo({ left: right - strip.clientWidth, behavior: "smooth" });
  }, [active, isMobile]);

  if (isMobile) {
    return (
      <div ref={stripRef} style={{
        display: "flex", flexDirection: "row", gap: 6, padding: 6, width: "100%", maxWidth: "100%", minWidth: 0,
        overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none",
        background: "rgba(255,255,255,.55)",
        backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)",
        border: "1px solid rgba(255,255,255,.65)", borderRadius: 18,
        boxShadow: "0 12px 30px -14px rgba(0,0,0,.2), inset 0 1px 0 rgba(255,255,255,.85)",
      }}>
        {items.map((it) => {
          const on = active === it.key;
          return (
            <button key={it.key} type="button" data-key={it.key}
              onClick={() => onSelect(it.key)}
              aria-current={on ? "page" : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 8, flexShrink: 0, minHeight: 44,
                padding: "6px 12px 6px 6px", border: "none", font: "inherit", cursor: "pointer",
                borderRadius: 13, whiteSpace: "nowrap",
                background: on ? "var(--surface)" : "transparent",
                boxShadow: on ? "var(--shadow)" : "none",
                transition: ".18s var(--ease)",
              }}>
              <span style={{
                width: 32, height: 32, flexShrink: 0, borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
                background: on ? "linear-gradient(150deg, #0B3DA8 0%, #0A84FF 100%)" : "rgba(118,118,128,.14)",
                boxShadow: on ? "0 6px 14px -4px rgba(10,90,210,.55)" : "0 1px 3px rgba(0,0,0,.08)",
              }}>{it.icon}</span>
              <span style={{ fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? "var(--text)" : "var(--text2)" }}>{it.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{
      position: "sticky", top: 60, alignSelf: "flex-start", flexShrink: 0,
      height: "calc(100vh - 60px)", display: "flex", alignItems: "center", marginTop: -28,
    }}>
    <div style={{
      display: "flex", flexDirection: "column", gap: 6, padding: 12, width: "100%",
      background: "rgba(255,255,255,.55)",
      backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: "1px solid rgba(255,255,255,.65)", borderRadius: 24,
      boxShadow: "0 24px 60px -18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.85)",
    }}>
      {items.map((it) => {
        const on = active === it.key;
        const hov = hover === it.key && !on;
        return (
          <div key={it.key}
            onClick={() => onSelect(it.key)}
            onMouseEnter={() => setHover(it.key)}
            onMouseLeave={() => setHover(null)}
            style={{
              display: "flex", alignItems: "center", gap: 13, width: 244, padding: "10px 14px 10px 10px",
              borderRadius: 16, cursor: "pointer",
              background: on ? "var(--surface)" : hov ? "rgba(255,255,255,.55)" : "transparent",
              boxShadow: on ? "var(--shadow)" : "none",
              transform: hov ? "translateX(3px)" : "none",
              transition: ".18s var(--ease)",
            }}>
            <div style={{
              width: 46, height: 46, flexShrink: 0, borderRadius: 13,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23,
              background: on ? "linear-gradient(150deg, #0B3DA8 0%, #0A84FF 100%)" : "rgba(118,118,128,.14)",
              boxShadow: on ? "0 10px 22px -6px rgba(10,90,210,.55)" : "0 1px 4px rgba(0,0,0,.08)",
              transform: (hov || on) ? "scale(1.06)" : "none", transition: ".18s var(--ease)",
            }}>{it.icon}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{it.label}</div>
              <div style={{ fontSize: 11.5, color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
    </div>
  );
}
